/**
 * versona_metrics_world.ts — the Versona Universe Metrics tab's WORLD groups
 * (2026-09-19): government, law, space, guilds, economy, PvP and crafting.
 *
 * `versona_metrics.ts` answers "who are these people" from the roster snapshot.
 * This file answers "what is the world they live in doing" from the systems
 * that surround them, and it is the same shape: PURE, in → out, no DB and no
 * DOM, so `verify:versona-metrics` can pin every number against rows it writes
 * itself. The module fetches and projects; this file only counts.
 *
 * Every row type here mirrors the PROJECTED columns the module asks for, not
 * the whole table (the Universe's law: never `SELECT *` a fat table — crime_case
 * is 2.4k rows, charter_clause 3.8k, market_round 7.3k).
 */
import { type Metric, type MetricGroup, stat, tally, gini, drill, int, f1, pct } from './versona_metrics.js';

// ── Government ───────────────────────────────────────────────────────────────
export interface WRace { state: string; winnerId: string }
export interface WCouncilTerm { termId: string; seats: number; closed: boolean }
export interface WCouncilSeat { termId: string; infTotal: number }
export interface WRecall { status: string; signatures: number; votesFor: number; votesAgainst: number }
export interface WCharter { kind: string; status: string; yesVotes: number; noVotes: number }
export interface WNorm { key: string; active: boolean; adoptionX10: number }
export interface WFedOffice { civ: string; office: string; holderName: string }
export interface WPromise { civ: string; kind: string; status: string }
export interface WTreaty { kind: string; status: string }
export interface WGoodwill { goodwill: number }
export interface WRivalry { aId: string; bId: string; grievance: number }
export interface WPetition { state: string; signatures: number; threshold: number }
export interface WMemorial { settlementId: string }
// ── Law ──────────────────────────────────────────────────────────────────────
export interface WCrime { kind: string; status: string; confidence: number; accusedId: string; settlementId: string }
// ── Space ────────────────────────────────────────────────────────────────────
export interface WSpaceLoc { mode: string; systemId: string }
export interface WSpaceStruct { kind: string }
export interface WSector { systemId: string; score: number }
export interface WShip { chassisKey: string; active: boolean }
export interface WComp { slot: string; quality: number }
export interface WWreck { chassisKey: string }
export interface WPilot { factionKey: string; rank: number; xp: number; tokens: number }
export interface WPilotSkill { skillKey: string; level: number }
export interface WMission { kind: string; state: string; rewardSov: number }
export interface WFirm { kind: string; hulls: number; hullsTarget: number; shortHours: number }
export interface WDeposit { oreKey: string; remaining: number; capacity: number }
export interface WShipment { state: string; qty: number; valueSov: number }
export interface WCampaign { state: string; winner: string; rounds: number }
export interface WKill { attackerKind: string; valueSov: number }
export interface WPort { state: string; sovEarned: number; sovSpent: number }
export interface WCourier { state: string; rewardSov: number }
// ── Guilds ───────────────────────────────────────────────────────────────────
export interface WGuild { guildId: string; name: string; tag: string }
export interface WGuildMember { guildId: string; title: string }
export interface WGuildVersona { guildId: string; versonaId: string; job: string }
export interface WGuildStore { guildId: string; key: string; qty: number }
export interface WGuildWar { state: string }
export interface WGuildElection { office: string; resolved: boolean }
// ── Economy ──────────────────────────────────────────────────────────────────
export interface WRound { settlementId: string; commodity: string; avgPrice: number; volume: number; unfilled: number; epoch: number }
export interface WTradeOrder { commodity: string; qty: number; premiumBps: number }
export interface WLink { trips: number }
export interface WStock { commodity: string; qty: number }
export interface WCoin { source: string; coins: number }
export interface WEscrow { state: string; amountSov: number }
// ── PvP ──────────────────────────────────────────────────────────────────────
export interface WDuel { state: string }
export interface WPvpRecord { duelsWon: number; duelsLost: number; duelsDrawn: number; matchesWon: number; matchesLost: number; objectives: number }
export interface WPvpSector { name: string; active: boolean; scoreA: number; scoreB: number }
// ── Crafting ─────────────────────────────────────────────────────────────────
export interface WMade { itemKey: string; quality: number }
export interface WSchem { schematicKey: string; source: string }
export interface WForge { status: string; fidelityTier: string }
export interface WExtractor { concentration: number; storedQty: number }
export interface WResNode { good: string; richness: number }
// ── Sim health ───────────────────────────────────────────────────────────────
/** One row of `sim_tick_ledger`: the world's pulse. */
export interface WTick { name: string; seq: number; lastAtMs: number; gapMicros: number }

export interface WorldMetricsInput {
  races: WRace[]; councilTerms: WCouncilTerm[]; councilSeats: WCouncilSeat[]; recalls: WRecall[];
  charter: WCharter[]; norms: WNorm[]; fedOffices: WFedOffice[]; promises: WPromise[];
  treaties: WTreaty[]; goodwill: WGoodwill[]; rivalries: WRivalry[]; petitions: WPetition[]; memorials: WMemorial[];
  crimes: WCrime[];
  spaceLocs: WSpaceLoc[]; spaceStructs: WSpaceStruct[]; sectors: WSector[];
  ships: WShip[]; comps: WComp[]; wrecks: WWreck[];
  pilots: WPilot[]; pilotSkills: WPilotSkill[]; missions: WMission[];
  firms: WFirm[]; deposits: WDeposit[]; shipments: WShipment[]; ports: WPort[]; couriers: WCourier[];
  campaigns: WCampaign[]; kills: WKill[];
  guilds: WGuild[]; guildMembers: WGuildMember[]; guildVersonas: WGuildVersona[];
  guildStores: WGuildStore[]; guildWars: WGuildWar[]; guildElections: WGuildElection[];
  rounds: WRound[]; tradeOrders: WTradeOrder[]; links: WLink[]; stocks: WStock[]; coins: WCoin[]; escrows: WEscrow[];
  duels: WDuel[]; pvpRecords: WPvpRecord[]; pvpSectors: WPvpSector[];
  made: WMade[]; schematics: WSchem[]; forge: WForge[]; extractors: WExtractor[]; nodes: WResNode[];
  ticks: WTick[];
  /** Wall clock, so tick staleness is measurable. 0 disables the sim card. */
  nowMs: number;
}
/**
 * When a tick counts as STOPPED. Self-calibrating, because a fixed number is
 * wrong in both directions: the ticks pace themselves at ~4.6 s, so 30 s looked
 * generous — until the Universe tab itself (an 11,600-row roster snapshot, the
 * 6.8 MB cognition set, the feed's 73k rows) stretched every gap past it and the
 * strip cried STOPPED three times at a perfectly healthy world. A tick is judged
 * against ITS OWN cadence, with a floor so a naturally slow tick is not held to
 * an impossible one.
 */
export const TICK_STALE_S = 45;
export const TICK_STALE_FACTOR = 10;
/** How long this tick may be quiet before it has stopped, given how fast it normally runs. */
export function tickStaleAfterS(gapMicros: number): number {
  return Math.max(TICK_STALE_S, (gapMicros / 1e6) * TICK_STALE_FACTOR);
}

/** An empty input — the module builds one of these and fills only what it fetched. */
export function emptyWorldInput(): WorldMetricsInput {
  return {
    races: [], councilTerms: [], councilSeats: [], recalls: [], charter: [], norms: [], fedOffices: [], promises: [],
    treaties: [], goodwill: [], rivalries: [], petitions: [], memorials: [], crimes: [],
    spaceLocs: [], spaceStructs: [], sectors: [], ships: [], comps: [], wrecks: [], pilots: [], pilotSkills: [], missions: [],
    firms: [], deposits: [], shipments: [], ports: [], couriers: [], campaigns: [], kills: [],
    guilds: [], guildMembers: [], guildVersonas: [], guildStores: [], guildWars: [], guildElections: [],
    rounds: [], tradeOrders: [], links: [], stocks: [], coins: [], escrows: [],
    duels: [], pvpRecords: [], pvpSectors: [], made: [], schematics: [], forge: [], extractors: [], nodes: [],
    ticks: [], nowMs: 0,
  };
}

/** `n` of `d` as a share, 0 when there is nothing to divide. */
const share = (n: number, d: number): number => (d > 0 ? n / d : 0);
/** Bars are printed with `toLocaleString`, so a summed float shows every digit it has. Round at the source. */
const bar = (label: string, value: number, total: number): { label: string; value: number; share: number } =>
  ({ label, value: Math.round(value), share: share(value, total) });
/** How many rows carry each value of one field (top `top`, biggest first). */
const by = <T>(rows: readonly T[], f: (r: T) => string, top = 12): Array<{ label: string; value: number; share: number }> =>
  tally(rows.map(f).filter((k) => k !== '')).slice(0, top);
/** A "x of y (z %)" line. */
const ofTotal = (label: string, n: number, d: number, hint?: string): Metric =>
  ({ label, value: d > 0 ? `${int(n)} of ${int(d)} · ${pct(share(n, d))}` : '—', hint });

/**
 * The world groups, in chip order. A group whose tables are all empty is
 * DROPPED rather than shown as a row of zeros — an admin reading this tab wants
 * to know what is happening, and an empty card is noise. (The federation and
 * town-score cards in `versona_metrics.ts` keep that same habit.)
 */
export function computeWorldMetrics(w: WorldMetricsInput): MetricGroup[] {
  const out: MetricGroup[] = [];
  const push = (g: MetricGroup, any: number): void => { if (any > 0) out.push(g); };

  // ── GOVERNMENT ─────────────────────────────────────────────────────────────
  {
    const done = w.races.filter((r) => r.state === 'done');
    const live = w.races.filter((r) => r.state !== 'done');
    const openTerm = w.councilTerms.filter((t) => !t.closed);
    const seatsOf = new Map<string, WCouncilSeat[]>();
    for (const s of w.councilSeats) { const a = seatsOf.get(s.termId) ?? []; a.push(s); seatsOf.set(s.termId, a); }
    const curSeats = openTerm.flatMap((t) => seatsOf.get(t.termId) ?? []);
    const infl = stat(curSeats.map((s) => s.infTotal));
    push({
      id: 'elections', cat: 'gov', title: 'Elections & councils',
      metrics: [
        { label: 'mayoral races run', value: int(w.races.length), hint: 'settlement_office elections: campaign → rallied → balloting → done' },
        { label: 'on the ballot NOW', value: int(live.length), hint: live.length ? by(live, (r) => r.state).map((b) => `${b.label} ${b.value}`).join(' · ') : 'no race is open' },
        ofTotal('races decided', done.length, w.races.length),
        { label: 'council terms · open', value: `${int(w.councilTerms.length)} · ${int(openTerm.length)}` },
        { label: 'seats this term', value: int(curSeats.length), hint: 'a seat is won with spent influence, not a headcount' },
        { label: 'influence per seat · mean / max', value: curSeats.length ? `${f1(infl.mean)} / ${f1(infl.max)}` : '—' },
        { label: 'recall petitions', value: int(w.recalls.length), hint: w.recalls.length ? by(w.recalls, (r) => r.status).map((b) => `${b.label} ${b.value}`).join(' · ') : '' },
      ],
      bars: by(w.races, (r) => r.state),
    }, w.races.length + w.councilTerms.length + w.recalls.length);

    const active = w.charter.filter((c) => c.status === 'active');
    const decided = w.charter.filter((c) => c.status !== 'proposed');
    const votes = w.charter.filter((c) => c.yesVotes + c.noVotes > 0);
    push({
      id: 'charter', cat: 'gov', title: 'Charter & norms',
      metrics: [
        { label: 'clauses put to a town', value: int(w.charter.length), hint: 'a charter clause is a standing rule a settlement voted on' },
        ofTotal('standing (active)', active.length, w.charter.length),
        ofTotal('rejected at the vote', w.charter.filter((c) => c.status === 'rejected').length, decided.length),
        { label: 'repealed since', value: int(w.charter.filter((c) => c.status === 'repealed').length) },
        { label: 'on the floor now', value: int(w.charter.filter((c) => c.status === 'proposed').length) },
        { label: 'mean yes share when voted', value: votes.length ? pct(stat(votes.map((c) => share(c.yesVotes, c.yesVotes + c.noVotes))).mean) : '—' },
        { label: 'norms written · active', value: `${int(w.norms.length)} · ${int(w.norms.filter((n) => n.active).length)}`, hint: 'a norm is a custom a town keeps without a vote' },
        { label: 'mean norm adoption', value: w.norms.length ? pct(stat(w.norms.map((n) => n.adoptionX10 / 10)).mean / 100) : '—' },
      ],
      bars: by(w.charter.filter((c) => c.status === 'active'), (c) => c.kind),
    }, w.charter.length + w.norms.length);

    const filled = w.fedOffices.filter((o) => o.holderName !== '');
    push({
      id: 'federal-offices', cat: 'gov', title: 'Federal offices & promises',
      metrics: [
        ofTotal('offices filled', filled.length, w.fedOffices.length, 'the seat\'s officers: Speaker, purse, the benches'),
        { label: 'promises made', value: int(w.promises.length), hint: 'a bill that passed owes a settlement something' },
        ...by(w.promises, (p) => p.status, 5).map((b) => ({ label: `promises ${b.label}`, value: `${int(b.value)} · ${pct(b.share)}` })),
      ],
      bars: by(w.fedOffices, (o) => o.office),
    }, w.fedOffices.length + w.promises.length);

    const gw = stat(w.goodwill.map((g) => g.goodwill));
    const rv = stat(w.rivalries.map((r) => r.grievance));
    const hot = w.rivalries.filter((r) => r.grievance >= 70).length;
    push({
      id: 'diplomacy', cat: 'gov', title: 'Treaties & standing between towns',
      metrics: [
        { label: 'treaties', value: int(w.treaties.length), hint: by(w.treaties, (t) => t.kind).map((b) => `${b.label} ${b.value}`).join(' · ') },
        ofTotal('still honoured', w.treaties.filter((t) => t.status === 'active').length, w.treaties.length),
        { label: 'broken', value: int(w.treaties.filter((t) => t.status === 'broken').length) },
        { label: 'goodwill pairs · mean', value: w.goodwill.length ? `${int(w.goodwill.length)} · ${f1(gw.mean)}` : '—' },
        { label: 'rivalries · mean grievance', value: w.rivalries.length ? `${int(w.rivalries.length)} · ${f1(rv.mean)}` : '—' },
        { label: 'grievance at 70+', value: int(hot), hint: 'a committed council declares at this heat — these are the wars waiting to happen',
          drill: drill('text', 'rivalries at 70+', w.rivalries.filter((r) => r.grievance >= 70).sort((x, y) => y.grievance - x.grievance).map((r) => `#${r.aId} \u2194 #${r.bId} \u00b7 ${r.grievance}`)) },
        { label: 'war petitions', value: int(w.petitions.length), hint: w.petitions.length ? `${w.petitions.filter((p) => p.signatures >= p.threshold).length} have reached threshold` : '' },
        { label: 'war memorials standing', value: int(w.memorials.length) },
      ],
      bars: by(w.treaties, (t) => `${t.kind} · ${t.status}`),
    }, w.treaties.length + w.goodwill.length + w.rivalries.length);
  }

  // ── LAW ────────────────────────────────────────────────────────────────────
  {
    const closed = w.crimes.filter((c) => c.status !== 'open');
    const guilty = w.crimes.filter((c) => c.status === 'guilty');
    const conf = stat(w.crimes.map((c) => c.confidence));
    push({
      id: 'justice', cat: 'law', title: 'Crime & justice',
      metrics: [
        { label: 'cases opened', value: int(w.crimes.length), hint: 'a crime_case is opened when witnesses agree something happened' },
        { label: 'open right now', value: int(w.crimes.filter((c) => c.status === 'open').length),
          drill: drill('versona', 'the accused, still open', w.crimes.filter((c) => c.status === 'open' && c.accusedId !== '' && c.accusedId !== '0').map((c) => c.accusedId)) },
        ofTotal('convicted', guilty.length, closed.length, 'of the cases that reached a verdict'),
        ofTotal('acquitted', w.crimes.filter((c) => c.status === 'acquitted').length, closed.length),
        { label: 'homicide / theft', value: `${int(w.crimes.filter((c) => c.kind === 'homicide').length)} / ${int(w.crimes.filter((c) => c.kind === 'theft').length)}` },
        { label: 'witness confidence · mean', value: w.crimes.length ? f1(conf.mean) : '—', hint: 'how sure the witnesses were; the Tribunal weighs it' },
      ],
      bars: by(w.crimes, (c) => `${c.kind} · ${c.status}`),
    }, w.crimes.length);
  }

  // ── SPACE ──────────────────────────────────────────────────────────────────
  {
    const sec = stat(w.sectors.map((s) => s.score));
    push({
      id: 'space-map', cat: 'space', title: 'Space · where everyone is',
      metrics: [
        { label: 'pilots located', value: int(w.spaceLocs.length) },
        { label: 'in flight / docked', value: `${int(w.spaceLocs.filter((l) => l.mode === 'flight').length)} / ${int(w.spaceLocs.filter((l) => l.mode === 'docked').length)}` },
        { label: 'systems occupied', value: int(new Set(w.spaceLocs.map((l) => l.systemId)).size) },
        { label: 'player structures deployed', value: int(w.spaceStructs.length), hint: by(w.spaceStructs, (s) => s.kind).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'sectors scored · mean / max', value: w.sectors.length ? `${int(w.sectors.length)} · ${f1(sec.mean)} / ${f1(sec.max)}` : '—' },
      ],
      bars: by(w.spaceLocs, (l) => `system ${l.systemId}`, 10),
    }, w.spaceLocs.length + w.spaceStructs.length + w.sectors.length);

    const q = stat(w.comps.map((c) => c.quality));
    push({
      id: 'space-fleet', cat: 'space', title: 'Space · the fleet',
      metrics: [
        { label: 'ships owned', value: int(w.ships.length) },
        ofTotal('flown (active hull)', w.ships.filter((s) => s.active).length, w.ships.length),
        { label: 'distinct chassis', value: int(new Set(w.ships.map((s) => s.chassisKey)).size) },
        { label: 'components fitted', value: int(w.comps.length) },
        { label: 'component quality · mean / max', value: w.comps.length ? `${f1(q.mean)} / ${f1(q.max)}` : '—' },
        { label: 'wrecks on the field', value: int(w.wrecks.length), hint: 'a wreck is lootable until it expires' },
      ],
      bars: by(w.ships, (s) => s.chassisKey),
    }, w.ships.length + w.comps.length);

    const xp = stat(w.pilots.map((p) => p.xp));
    const lvl = stat(w.pilotSkills.map((s) => s.level));
    push({
      id: 'space-pilots', cat: 'space', title: 'Space · pilots & missions',
      metrics: [
        { label: 'pilots enrolled', value: int(w.pilots.length) },
        // An unset faction_key is NOT a faction. Counting the empty string as one
        // read "3 factions served" while 56 of 63 pilots had picked nobody.
        { label: 'factions served', value: int(new Set(w.pilots.map((p) => p.factionKey).filter((k) => k !== '')).size) },
        ofTotal('sworn to a faction', w.pilots.filter((p) => p.factionKey !== '').length, w.pilots.length, 'the rest fly unaligned'),
        { label: 'rank · mean / max', value: w.pilots.length ? `${f1(stat(w.pilots.map((p) => p.rank)).mean)} / ${int(stat(w.pilots.map((p) => p.rank)).max)}` : '—' },
        { label: 'xp · mean / max', value: w.pilots.length ? `${int(xp.mean)} / ${int(xp.max)}` : '—' },
        { label: 'skills trained · mean level', value: w.pilotSkills.length ? `${int(w.pilotSkills.length)} · ${f1(lvl.mean)}` : '—' },
        { label: 'missions taken', value: int(w.missions.length), hint: by(w.missions, (m) => m.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'mission reward · total', value: int(w.missions.reduce((a, m) => a + m.rewardSov, 0)) },
      ],
      bars: by(w.pilots, (p) => p.factionKey),
    }, w.pilots.length + w.missions.length);

    const shipped = w.shipments.reduce((a, s) => a + s.valueSov, 0);
    const lost = w.shipments.filter((s) => s.state === 'lost');
    const dep = w.deposits.length ? stat(w.deposits.map((d) => share(d.remaining, d.capacity))) : null;
    push({
      id: 'space-industry', cat: 'space', title: 'Space · industry & freight',
      metrics: [
        { label: 'firms running', value: int(w.firms.length), hint: by(w.firms, (f) => f.kind).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'hulls built / wanted', value: `${int(w.firms.reduce((a, f) => a + f.hulls, 0))} / ${int(w.firms.reduce((a, f) => a + f.hullsTarget, 0))}`, hint: 'a firm short of its target is starved of ore' },
        { label: 'firm short-hours', value: int(w.firms.reduce((a, f) => a + f.shortHours, 0)), hint: 'hours a firm sat idle for want of input' },
        { label: 'belt deposits · mean left', value: dep ? `${int(w.deposits.length)} · ${pct(dep.mean)}` : '—' },
        { label: 'shipments', value: int(w.shipments.length), hint: by(w.shipments, (s) => s.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'value in transit', value: int(shipped) },
        ofTotal('lost to ambush', lost.length, w.shipments.length),
        { label: 'ports · earned / spent', value: w.ports.length ? `${int(w.ports.reduce((a, p) => a + p.sovEarned, 0))} / ${int(w.ports.reduce((a, p) => a + p.sovSpent, 0))}` : '—' },
        { label: 'courier contracts', value: int(w.couriers.length), hint: by(w.couriers, (c) => c.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
      ],
      bars: by(w.deposits, (d) => d.oreKey),
    }, w.firms.length + w.shipments.length + w.deposits.length + w.ports.length);

    const killValue = w.kills.reduce((a, k) => a + k.valueSov, 0);
    push({
      id: 'space-conflict', cat: 'space', title: 'Space · campaigns & kills',
      metrics: [
        { label: 'campaigns', value: int(w.campaigns.length), hint: by(w.campaigns, (c) => c.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'rounds fought · mean', value: w.campaigns.length ? f1(stat(w.campaigns.map((c) => c.rounds)).mean) : '—' },
        { label: 'ships killed', value: int(w.kills.length) },
        { label: 'value destroyed', value: int(killValue), hint: 'hull plus fitted components, at the killmail\'s valuation' },
        { label: 'mean killmail', value: w.kills.length ? int(killValue / w.kills.length) : '—' },
      ],
      bars: by(w.kills, (k) => k.attackerKind),
    }, w.campaigns.length + w.kills.length);
  }

  // ── GUILDS ─────────────────────────────────────────────────────────────────
  {
    const memberOf = new Map<string, number>();
    for (const m of w.guildMembers) memberOf.set(m.guildId, (memberOf.get(m.guildId) ?? 0) + 1);
    for (const v of w.guildVersonas) memberOf.set(v.guildId, (memberOf.get(v.guildId) ?? 0) + 1);
    const sizes = w.guilds.map((g) => memberOf.get(g.guildId) ?? 0);
    const sz = stat(sizes);
    const stock = w.guildStores.reduce((a, s) => a + s.qty, 0);
    const named = new Map(w.guilds.map((g) => [g.guildId, g.tag || g.name]));
    push({
      id: 'guilds', cat: 'guilds', title: 'Guilds',
      metrics: [
        { label: 'guilds founded', value: int(w.guilds.length) },
        { label: 'player members', value: int(w.guildMembers.length) },
        { label: 'versonas recruited', value: int(w.guildVersonas.length), hint: 'a versona hired into a guild works a real job for it' },
        { label: 'size · mean / max', value: w.guilds.length ? `${f1(sz.mean)} / ${int(sz.max)}` : '—' },
        { label: 'empty halls', value: int(sizes.filter((n) => n === 0).length), hint: 'founded, never staffed',
          drill: drill('text', 'halls with nobody in them', w.guilds.filter((g) => (memberOf.get(g.guildId) ?? 0) === 0).map((g) => g.tag ? `${g.name} [${g.tag}]` : g.name)) },
        { label: 'size inequality (gini)', value: w.guilds.length ? f1(gini(sizes)) : '—', hint: '0 = every guild the same size, 1 = one guild holds everyone' },
        { label: 'store goods held', value: int(stock), hint: `${w.guildStores.length} stacks across ${new Set(w.guildStores.map((s) => s.guildId)).size} halls` },
        { label: 'guild wars', value: int(w.guildWars.length), hint: by(w.guildWars, (g) => g.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'elections · resolved', value: `${int(w.guildElections.length)} · ${int(w.guildElections.filter((e) => e.resolved).length)}` },
      ],
      bars: w.guilds
        .map((g) => ({ label: named.get(g.guildId) ?? g.guildId, value: memberOf.get(g.guildId) ?? 0 }))
        .filter((b) => b.value > 0).sort((a, b) => b.value - a.value).slice(0, 12)
        .map((b) => bar(b.label, b.value, w.guildMembers.length + w.guildVersonas.length)),
    }, w.guilds.length);

    push({
      id: 'guild-work', cat: 'guilds', title: 'Guild workforce (by trade)',
      metrics: [
        { label: 'distinct trades worked', value: int(new Set(w.guildVersonas.map((v) => v.job)).size) },
        { label: 'versonas on the books', value: int(w.guildVersonas.length), drill: drill('versona', 'guild workforce', w.guildVersonas.map((v) => v.versonaId)) },
      ],
      bars: by(w.guildVersonas, (v) => v.job),
    }, w.guildVersonas.length);
  }

  // ── ECONOMY ────────────────────────────────────────────────────────────────
  {
    // market_round is an APPEND log — only the newest epoch is "the market now".
    const lastEpoch = w.rounds.reduce((m, r) => Math.max(m, r.epoch), -Infinity);
    const now = w.rounds.filter((r) => r.epoch === lastEpoch);
    const vol = now.reduce((a, r) => a + r.volume, 0);
    const unfilled = now.reduce((a, r) => a + r.unfilled, 0);
    push({
      id: 'markets', cat: 'econ', title: 'Markets (latest round)',
      metrics: [
        { label: 'rounds on record', value: int(w.rounds.length) },
        { label: 'commodities traded', value: int(new Set(now.map((r) => r.commodity)).size) },
        { label: 'towns clearing', value: int(new Set(now.map((r) => r.settlementId)).size), hint: 'one row per settlement per commodity per round' },
        { label: 'volume this round', value: int(vol) },
        ofTotal('demand left unfilled', unfilled, vol + unfilled, 'orders the round could not satisfy — a shortage you can see'),
        { label: 'mean price', value: now.length ? f1(stat(now.map((r) => r.avgPrice)).mean) : '—' },
        { label: 'open trade orders', value: int(w.tradeOrders.length), hint: w.tradeOrders.length ? `mean premium ${f1(stat(w.tradeOrders.map((o) => o.premiumBps)).mean / 100)} %` : '' },
        { label: 'market links · trips', value: `${int(w.links.length)} · ${int(w.links.reduce((a, l) => a + l.trips, 0))}`, hint: 'a caravan route that has actually been walked' },
      ],
      bars: (() => {
        const perCommodity = new Map<string, number>();
        for (const r of now) perCommodity.set(r.commodity, (perCommodity.get(r.commodity) ?? 0) + r.volume);
        const total = [...perCommodity.values()].reduce((a, b) => a + b, 0);
        return [...perCommodity.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([l, v]) => bar(l, v, total));
      })(),
    }, w.rounds.length);

    const stockTotal = w.stocks.reduce((a, s) => a + s.qty, 0);
    const minted = w.coins.reduce((a, c) => a + c.coins, 0);
    push({
      id: 'stores', cat: 'econ', title: 'Stores, coin & escrow',
      metrics: [
        { label: 'settlement stock rows', value: int(w.stocks.length) },
        { label: 'goods in store', value: int(stockTotal) },
        { label: 'coin issued', value: int(minted), hint: w.coins.length ? by(w.coins, (c) => c.source).map((b) => `${b.label} ${b.value}`).join(' · ') : 'coins only MOVE — issuance is the one source' },
        { label: 'escrows held', value: int(w.escrows.length), hint: w.escrows.length ? by(w.escrows, (e) => e.state).map((b) => `${b.label} ${b.value}`).join(' · ') : '' },
        { label: 'value in escrow', value: int(w.escrows.reduce((a, e) => a + e.amountSov, 0)) },
      ],
      bars: (() => {
        const per = new Map<string, number>();
        for (const s of w.stocks) per.set(s.commodity, (per.get(s.commodity) ?? 0) + s.qty);
        return [...per.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([l, v]) => bar(l, v, stockTotal));
      })(),
    }, w.stocks.length + w.coins.length);
  }

  // ── PVP ────────────────────────────────────────────────────────────────────
  {
    const duelsW = w.pvpRecords.reduce((a, r) => a + r.duelsWon, 0);
    const duelsL = w.pvpRecords.reduce((a, r) => a + r.duelsLost, 0);
    const duelsD = w.pvpRecords.reduce((a, r) => a + r.duelsDrawn, 0);
    push({
      id: 'pvp', cat: 'pvp', title: 'Player versus player',
      metrics: [
        { label: 'duels on record', value: int(w.duels.length), hint: by(w.duels, (d) => d.state).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'fighters with a record', value: int(w.pvpRecords.length) },
        { label: 'duels won / lost / drawn', value: `${int(duelsW)} / ${int(duelsL)} / ${int(duelsD)}`, hint: 'wins and losses are the same fights counted from both sides' },
        { label: 'arena matches won', value: int(w.pvpRecords.reduce((a, r) => a + r.matchesWon, 0)) },
        { label: 'objectives taken', value: int(w.pvpRecords.reduce((a, r) => a + r.objectives, 0)) },
        { label: 'sectors · live', value: `${int(w.pvpSectors.length)} · ${int(w.pvpSectors.filter((s) => s.active).length)}` },
      ],
      // The share here is side A's share of the sector, not the sector's share of the board.
      bars: w.pvpSectors.filter((s) => s.scoreA + s.scoreB > 0)
        .map((s) => ({ label: s.name || 'sector', value: Math.round(s.scoreA + s.scoreB), share: share(s.scoreA, s.scoreA + s.scoreB) })).slice(0, 12),
    }, w.duels.length + w.pvpRecords.length + w.pvpSectors.length);
  }

  // ── CRAFTING ───────────────────────────────────────────────────────────────
  {
    const mq = stat(w.made.map((m) => m.quality));
    const stored = w.extractors.reduce((a, e) => a + e.storedQty, 0);
    const rich = stat(w.nodes.map((n) => n.richness));
    push({
      id: 'crafting', cat: 'craft', title: 'Crafting & industry',
      metrics: [
        { label: 'items made by versonas', value: int(w.made.length), hint: 'versona_made_item — the craft that earns a title' },
        { label: 'quality · mean / max', value: w.made.length ? `${f1(mq.mean)} / ${f1(mq.max)}` : '—' },
        { label: 'distinct items', value: int(new Set(w.made.map((m) => m.itemKey)).size) },
        { label: 'player schematics known', value: int(w.schematics.length), hint: w.schematics.length ? by(w.schematics, (s) => s.source).map((b) => `${b.label} ${b.value}`).join(' · ') : '' },
        { label: 'forge projects', value: int(w.forge.length), hint: by(w.forge, (f) => f.status).map((b) => `${b.label} ${b.value}`).join(' · ') },
        { label: 'extractors · stored', value: `${int(w.extractors.length)} · ${int(stored)}` },
        { label: 'resource nodes · mean richness', value: w.nodes.length ? `${int(w.nodes.length)} · ${f1(rich.mean)}` : '—' },
      ],
      bars: by(w.made, (m) => m.itemKey),
    }, w.made.length + w.schematics.length + w.forge.length + w.nodes.length);
  }

  // ── SIM HEALTH ─────────────────────────────────────────────────────────────
  //
  // WHY THIS CARD EXISTS: chasing "the versonas in a ground battle stop moving"
  // (2026-09-19) the hard part was not the bug, it was being unable to answer
  // "is the world even ticking?" without a harness. `sim_tick_ledger` has
  // carried `seq`, `last_at` and `gap_micros` the whole time and nothing drew
  // it. Four rows.
  if (w.ticks.length > 0 && w.nowMs > 0) {
    const age = (t: WTick): number => (w.nowMs - t.lastAtMs) / 1000;
    const stale = w.ticks.filter((t) => age(t) > tickStaleAfterS(t.gapMicros));
    const rows: Metric[] = [
      { label: 'ticks running', value: `${int(w.ticks.length - stale.length)} of ${int(w.ticks.length)}`, hint: `a tick counts as stopped once it is quiet for ${TICK_STALE_FACTOR}\u00d7 its own cadence (never less than ${TICK_STALE_S} s)` },
    ];
    for (const t of [...w.ticks].sort((a, b) => a.name.localeCompare(b.name))) {
      const a = age(t), gap = t.gapMicros / 1e6;
      rows.push({
        label: t.name,
        value: `${a > tickStaleAfterS(t.gapMicros) ? 'STOPPED · ' : ''}${f1(a)} s ago · every ${f1(gap)} s`,
        hint: `seq ${int(t.seq)} — the sequence the tick guard counts on`,
      });
    }
    push({
      id: 'sim-ticks', cat: 'sim', title: stale.length > 0 ? `Sim health \u2014 ${stale.length} TICK STOPPED` : 'Sim health',
      metrics: rows,
      bars: w.ticks.map((t) => ({ label: t.name, value: Math.round(t.gapMicros / 1e6 * 10) / 10, share: Math.min(1, age(t) / tickStaleAfterS(t.gapMicros)) })),
    }, w.ticks.length);
  }

  return out;
}
